import { make } from 'vuex-pathify'
import store from '../index'
import { getQueryFromFilter, showError } from '@/util/helpers'
import { removeFormatDate } from '@/util/formatters'
import axios from 'axios'
import { STATUS_FATURADO } from '@/enums/request'
import { PERMISSIONS } from '@/enums/permissions'
import { TIPO_DESPESA } from '@/enums/payments'

const baseChartOptions = {
  axisX: {
    showGrid: false,
    labelInterpolationFnc: value => {
      const label = String(value)
      const partial = label.includes('(parcial)')
      const shortLabel = label.includes(' - ') ? label.slice(0, 5) : label.replace(' (parcial)', '')
      return `${shortLabel}${partial ? ' *' : ''}`
    },
  },
  low: 0,
  lineSmooth: false,
  chartPadding: {
    top: 10,
    right: 15,
    bottom: 0,
    left: 0,
  },
}

const responsiveOptions = [
  [
    'screen and (max-width: 640px)',
    {
      seriesBarDistance: 5,
      axisX: {
        labelInterpolationFnc: function (value) {
          return `${String(value).slice(0, 5)}${String(value).includes('(parcial)') ? ' *' : ''}`
        },
      },
    },
  ],
]

const chartConfigById = {
  requests: {
    options: { ...baseChartOptions },
    seriesLabels: ['Pedidos realizados', 'Pedidos faturados'],
    valueFormats: ['number', 'number'],
  },
  cashflow: {
    options: { ...baseChartOptions },
    seriesLabels: ['Receitas', 'Despesas'],
    valueFormats: ['currency', 'currency'],
  },
  customers: {
    options: { ...baseChartOptions },
    seriesLabels: ['Novos ou reativados', 'Retidos', 'Saíram da carteira'],
    valueFormats: ['number', 'number', 'number'],
  },
  recurrence: {
    options: { ...baseChartOptions },
    seriesLabels: ['MRR', 'CAC'],
    valueFormats: ['currency', 'currency'],
  },
}

const ANALYTICS_WIDGET_KEYS = [
  'requests',
  'invoicedRequests',
  'schedules',
  'mrr',
  'mrrGrowth',
  'retentionRate',
  'churnRate',
  'ltv',
  'cac',
  'requestsChart',
  'cashflow',
  'customers',
  'recurrence',
  'productRanking',
  'averageTicketBySeller',
  'customerRanking',
  'sellerRanking',
]

const REQUESTS_PANEL_WIDGET_KEY = 'lastRequests'
const GOALS_SCHEDULES_WIDGET_KEY = 'goalsSchedules'

const normalizeWidgetKeys = keys => [...new Set((keys || []).filter(Boolean))]
const emptyPanels = () => ({
  productRanking: { best: [], worst: [], servicesBest: [], servicesWorst: [] },
  averageTicketBySeller: [],
  customerRanking: { best: [], worst: [] },
  sellerRanking: { best: [], worst: [] },
})
const normalizePanels = panels => ({
  ...emptyPanels(),
  ...(panels || {}),
  productRanking: {
    ...emptyPanels().productRanking,
    ...(panels?.productRanking || {}),
  },
  customerRanking: {
    ...emptyPanels().customerRanking,
    ...(panels?.customerRanking && !Array.isArray(panels.customerRanking)
      ? panels.customerRanking
      : {}),
  },
  sellerRanking: {
    ...emptyPanels().sellerRanking,
    ...(panels?.sellerRanking || {}),
  },
})
const hasAnyVisibleWidget = (visibleWidgetKeys, widgetKeys) => widgetKeys.some(key => visibleWidgetKeys.includes(key))
const getScopedUserId = () => (
  store.get('authStore@userPermissions').includes(PERMISSIONS.DASHBOARD)
    ? null
    : store.get('authStore@currentUser')?.id
)

const getChartHigh = (series = []) => {
  const max = series.flat().reduce((acc, value) => Math.max(acc, Number(value || 0)), 0)
  if (!max) {
    return 10
  }
  return Math.ceil(max * 1.15)
}

const currencyAxisFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  notation: 'compact',
  maximumFractionDigits: 1,
})

const mapChart = chart => {
  const config = chartConfigById[chart.id] || { options: { ...baseChartOptions } }

  return {
    ...chart,
    data: {
      labels: chart.labels || [],
      series: chart.series || [],
    },
    options: {
      ...config.options,
      high: getChartHigh(chart.series),
      axisY: config.valueFormats?.includes('currency')
        ? {
          offset: 70,
          labelInterpolationFnc: value => currencyAxisFormatter.format(value),
        }
        : {
          offset: 40,
          onlyInteger: true,
          labelInterpolationFnc: value => Number.isInteger(value) ? value : '',
        },
    },
    seriesLabels: config.seriesLabels || [],
    valueFormats: config.valueFormats || [],
    responsiveOptions,
    time: 'Atualizado agora',
  }
}

const state = {
  charts: [],

  panels: emptyPanels(),

  stats: [],

  acquisitionClassifications: [],

  availableWidgets: [],

  visibleWidgetKeys: [],

  savedVisibleWidgetKeys: [],

  filter: {
    rangePreset: '30d',
    groupBy: 'week',
    startDate: null,
    endDate: null,
    acquisitionClassificationIds: [],
  },

  loading: false,

  preferencesLoading: false,
}

const mutations = make.mutations(state)

const actions = {
  load: async ({ state, dispatch }) => {
    const userId = getScopedUserId()

    state.loading = true
    try {
      await Promise.all([
        dispatch('loadAcquisitionClassifications'),
        dispatch('loadPreferences'),
      ])
      await dispatch('loadVisibleData', { userId, force: true })
    } catch (e) {
      showError(e)
    } finally {
      state.loading = false
    }
  },

  loadAcquisitionClassifications: async ({ state }) => {
    const result = await store.dispatch('classificationStore/get', {
      page: 1,
      itemsPerPage: 1000,
      sortBy: ['description'],
      sortDesc: [false],
      type: TIPO_DESPESA,
    })

    state.acquisitionClassifications = result?.data || []
  },

  loadPreferences: async ({ state }) => {
    state.preferencesLoading = true

    try {
      const res = await axios.get(`${process.env.VUE_APP_API_URL}/dashboard/preferences`)
      const visibleWidgetKeys = normalizeWidgetKeys(res.data?.visibleWidgetKeys)

      state.availableWidgets = res.data?.widgets || []
      state.visibleWidgetKeys = visibleWidgetKeys
      state.savedVisibleWidgetKeys = [...visibleWidgetKeys]
      return res.data
    } catch (e) {
      showError(e)
      throw e
    } finally {
      state.preferencesLoading = false
    }
  },

  getAnalytics: async ({ state }, { userId } = {}) => {
    const payload = {
      ...state.filter,
      userId,
      startDate: state.filter.startDate ? removeFormatDate(state.filter.startDate) : null,
      endDate: state.filter.endDate ? removeFormatDate(state.filter.endDate) : null,
      acquisitionClassificationIds: state.filter.acquisitionClassificationIds,
      visibleWidgetKeys: state.visibleWidgetKeys,
    }

    state.loading = true
    try {
      const res = await axios.get(`${process.env.VUE_APP_API_URL}/dashboard/analytics?${getQueryFromFilter(payload)}`)

      state.stats = (res.data.summary || []).map(item => ({
        ...item,
        actionText: item.description,
      }))
      state.charts = (res.data.charts || []).map(mapChart)
      state.panels = normalizePanels(res.data.panels)
      return res.data
    } catch (e) {
      showError(e)
      throw e
    } finally {
      state.loading = false
    }
  },

  loadVisibleData: async ({ state, dispatch }, { userId, force = false } = {}) => {
    const resolvedUserId = userId === undefined ? getScopedUserId() : userId

    const tasks = []
    const hasAnalyticsWidgets = hasAnyVisibleWidget(state.visibleWidgetKeys, ANALYTICS_WIDGET_KEYS)
    const hasRequestsPanel = state.visibleWidgetKeys.includes(REQUESTS_PANEL_WIDGET_KEY)
    const hasGoalsSchedulesPanel = state.visibleWidgetKeys.includes(GOALS_SCHEDULES_WIDGET_KEY)

    if (hasAnalyticsWidgets) {
      tasks.push(dispatch('getAnalytics', { userId: resolvedUserId }))
    } else if (force) {
      state.stats = []
      state.charts = []
      state.panels = emptyPanels()
    }

    if (hasRequestsPanel) {
      tasks.push(dispatch('getAuxiliary', { userId: resolvedUserId }))
    }

    if (hasGoalsSchedulesPanel) {
      if (!hasRequestsPanel) {
        tasks.push(dispatch('getAuxiliary', { userId: resolvedUserId }))
      }
    }

    if (tasks.length === 0) {
      return null
    }

    return Promise.all(tasks)
  },

  refresh: async ({ dispatch }) => {
    return dispatch('loadVisibleData', { userId: getScopedUserId(), force: true })
  },

  resetFilter: async ({ state, dispatch }) => {
    state.filter = {
      rangePreset: '30d',
      groupBy: 'week',
      startDate: null,
      endDate: null,
      acquisitionClassificationIds: [],
    }

    await dispatch('refresh')
  },

  syncVisibleWidgets: async ({ state, dispatch }, visibleWidgetKeys) => {
    const nextKeys = normalizeWidgetKeys(visibleWidgetKeys)
    const previousKeys = [...state.savedVisibleWidgetKeys]
    const hadAnalyticsWidgets = hasAnyVisibleWidget(previousKeys, ANALYTICS_WIDGET_KEYS)
    const hasAnalyticsWidgets = hasAnyVisibleWidget(nextKeys, ANALYTICS_WIDGET_KEYS)
    const addedAnalyticsWidget = nextKeys.some(key => ANALYTICS_WIDGET_KEYS.includes(key) && !previousKeys.includes(key))
    const hadRequestsPanel = previousKeys.includes(REQUESTS_PANEL_WIDGET_KEY)
    const hasRequestsPanel = nextKeys.includes(REQUESTS_PANEL_WIDGET_KEY)
    const hadGoalsSchedulesPanel = previousKeys.includes(GOALS_SCHEDULES_WIDGET_KEY)
    const hasGoalsSchedulesPanel = nextKeys.includes(GOALS_SCHEDULES_WIDGET_KEY)
    const scopedUserId = getScopedUserId()

    state.preferencesLoading = true
    state.visibleWidgetKeys = nextKeys

    try {
      const res = await axios.put(`${process.env.VUE_APP_API_URL}/dashboard/preferences`, {
        visibleWidgetKeys: nextKeys,
      })

      const savedKeys = normalizeWidgetKeys(res.data?.visibleWidgetKeys)
      state.availableWidgets = res.data?.widgets || state.availableWidgets
      state.visibleWidgetKeys = savedKeys
      state.savedVisibleWidgetKeys = [...savedKeys]

      if (!hasAnalyticsWidgets) {
        state.stats = []
        state.charts = []
        state.panels = emptyPanels()
      } else if (!hadAnalyticsWidgets || addedAnalyticsWidget) {
        await dispatch('getAnalytics', { userId: scopedUserId })
      }

      const needsAuxiliary = (hasRequestsPanel && !hadRequestsPanel) ||
        (hasGoalsSchedulesPanel && !hadGoalsSchedulesPanel)
      if (needsAuxiliary) {
        await dispatch('getAuxiliary', { userId: scopedUserId })
      }

      return savedKeys
    } catch (e) {
      state.visibleWidgetKeys = [...state.savedVisibleWidgetKeys]
      showError(e)
      throw e
    } finally {
      state.preferencesLoading = false
    }
  },

  getAuxiliary: async (context, { userId }) => {
    const res = await axios.get(`${process.env.VUE_APP_API_URL}/dashboard/auxiliary?${getQueryFromFilter({ userId })}`)
    store.set('requestStore@requests', res.data?.requests || { data: [], count: 0, limit: 5 })
    store.set('goalStore@goals', res.data?.goals || { data: [], count: 0, limit: 5 })
    store.set('scheduleStore@schedules', res.data?.schedules || { data: [], count: 0, limit: 5 })
    return res.data
  },
}

const getters = {
  ...make.getters(state),
  defaultInvoicedRoute: () => `pedidos?status=${STATUS_FATURADO}`,
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}
